import { useMutationObserver, watchImmediate, watchOnce } from "@vueuse/core";
import { ref } from "vue";

/**
 * Sets the fill color of the SVG.
 * @param {import('vue').Ref<HTMLElement|null>} root - The root element (the element to apply the fill color to)
 * @param {import('vue').Ref<string>} fillColor - The fill color (a ref or computed).
 */
export function useSvgFillColor(root, fillColor) {
  const mutationObserverStop = ref(null);

  watchImmediate(fillColor, (value) => {
    // Always clean up previous observer before reconfiguring
    mutationObserverStop.value?.();
    mutationObserverStop.value = null;

    if (!value) {
      // Prevent stale/ghost fill when fillColor becomes falsy
      root.value?.querySelectorAll("path, use").forEach((path) => {
        path.removeAttribute("fill");
      });
      return;
    }

    /**
     * If a fill color prop was set, we need to update the fill color of the SVG.
     * We wait until the color composable has detected the color to be used which results in a CSS variable being set.
     * Once the variable is set, the observer will be notified and the setFillColor function will be called.
     */
    const { stop } = useMutationObserver(root, setFillColor, {
      attributes: true,
      attributeFilter: ["style"],
      subtree: true,
      childList: true,
    });
    mutationObserverStop.value = stop;

    watchOnce(root, (value) => {
      if (value) {
        setFillColor();
      }
    });

    function setFillColor() {
      if (!root.value) {
        return;
      }

      const property = "fill";
      const color = root.value.style.getPropertyValue(`--${property}`);

      root.value.querySelectorAll("path, use").forEach((path) => {
        if (color) {
          path.setAttribute(property, color);
        } else {
          path.removeAttribute(property);
        }
      });
    }
  });
}
